import { useCallback, useEffect, useMemo, useState } from "react";
import { useAccount, useConfig, useConnect, useDisconnect } from "wagmi";
import { readContract, waitForTransactionReceipt, writeContract } from "wagmi/actions";
import { base } from "wagmi/chains";
import { getAddress, parseUnits, type Hex } from "viem";
import { captureX402EndpointUrl } from "../../lib/x402Discovery";
import { X402PublishDetails } from "./X402PublishDetails";
import { X402ReviewComposer } from "./X402ReviewComposer";

// Seeding an x402 endpoint is a routed Boon: it burns a fixed 100,000 $BOON and
// sends a small USDC gratuity to the endpoint's own payout wallet. That on-chain
// recognition is what makes the endpoint discoverable and unlocks wallet reviews
// A self-reported review alone cannot create a listing.
const BURN_AMOUNT = 100_000n * 10n ** 18n;
const BUY_BOON_URL = "https://app.uniswap.org/swap?outputCurrency=0x5Bec0bD17D16641660D66d82da4cF78b46B9EBA3&chain=base";

const erc20Abi = [
  { type: "function", name: "approve", stateMutability: "nonpayable", inputs: [{ name: "spender", type: "address" }, { name: "amount", type: "uint256" }], outputs: [{ name: "", type: "bool" }] },
  { type: "function", name: "allowance", stateMutability: "view", inputs: [{ name: "owner", type: "address" }, { name: "spender", type: "address" }], outputs: [{ name: "", type: "uint256" }] },
  { type: "function", name: "balanceOf", stateMutability: "view", inputs: [{ name: "account", type: "address" }], outputs: [{ name: "", type: "uint256" }] },
] as const;

const routedBoonAbi = [
  {
    type: "function",
    name: "sendRoutedBoon",
    stateMutability: "nonpayable",
    inputs: [
      { name: "routeId", type: "bytes32" },
      { name: "contextRef", type: "bytes32" },
      { name: "endpointPayTo", type: "address" },
      { name: "endpointGratuityUsdc", type: "uint256" },
      { name: "networkPayTo", type: "address" },
      { name: "networkGratuityUsdc", type: "uint256" },
    ],
    outputs: [],
  },
] as const;

function envAddress(name: "VITE_X402_ROUTED_BOON_CONTRACT" | "VITE_USDC_CONTRACT" | "VITE_BOON_TOKEN_ADDRESS"): `0x${string}` | null {
  const raw = (import.meta.env[name] as string | undefined)?.trim();
  if (!raw || !/^0x[0-9a-fA-F]{40}$/.test(raw) || /^0x0{40}$/.test(raw)) return null;
  return getAddress(raw);
}

function randomContextRef(): Hex {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return `0x${Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("")}` as Hex;
}

function formatBoon(amount: bigint): string {
  return (amount / 10n ** 18n).toLocaleString();
}

/**
 * Browser flow for a $BOON holder to recognize (seed) an unlisted x402 endpoint:
 * approve $BOON + USDC, then sendRoutedBoon, which burns 100,000 $BOON and sends
 * the gratuity to the endpoint. After it lands the endpoint is discoverable.
 */
export function X402RecognizeEndpoint({
  routeId,
  endpointUrl,
  method,
  endpointPayTo,
  isX402,
  contract,
  gratuityDefaultUsdc = "0.10",
  onRecognized,
}: {
  routeId: string;
  endpointUrl: string;
  method: string;
  endpointPayTo: string | null;
  isX402: boolean | null;
  contract: string | null;
  gratuityDefaultUsdc?: string;
  onRecognized: () => void;
}) {
  const config = useConfig();
  const { address, isConnected, chainId, connector } = useAccount();
  const { connectAsync, connectors } = useConnect();
  const { disconnectAsync } = useDisconnect();
  const [connecting, setConnecting] = useState<string | null>(null);
  const [connectError, setConnectError] = useState<string | null>(null);
  const connectChoices = useMemo(() => [
    { kind: "coinbase", label: "Coinbase", connector: connectors.find((c) => c.id === "coinbaseWallet") ?? connectors.find((c) => /coinbase/i.test(c.name)) },
    { kind: "metamask", label: "Metamask", connector: connectors.find((c) => /metamask/i.test(c.name) || c.id === "metaMask" || c.type === "metaMask") ?? connectors.find((c) => c.type === "injected") },
    { kind: "walletConnect", label: "WalletConnect", connector: connectors.find((c) => c.id === "walletConnect") ?? connectors.find((c) => /walletconnect|reown/i.test(c.name)) },
  ].filter((c) => c.connector), [connectors]);
  async function connectWallet(choice: (typeof connectChoices)[number]) {
    if (!choice.connector || connecting) return;
    setConnecting(choice.kind);
    setConnectError(null);
    try {
      if (isConnected && connector?.id !== choice.connector.id) await disconnectAsync();
      await connectAsync({ connector: choice.connector, chainId: base.id });
    } catch (cause) {
      setConnectError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setConnecting(null);
    }
  }
  const routedBoon = envAddress("VITE_X402_ROUTED_BOON_CONTRACT");
  const usdc = envAddress("VITE_USDC_CONTRACT");
  const boon = envAddress("VITE_BOON_TOKEN_ADDRESS");

  // Manual payout address, used when the scan can't read the endpoint's payTo
  // (e.g. it wasn't reachable) but the recognizer knows where to send gratuity.
  const [manualPayTo, setManualPayTo] = useState("");
  const resolvedPayTo = endpointPayTo ?? (/^0x[0-9a-fA-F]{40}$/.test(manualPayTo.trim()) ? getAddress(manualPayTo.trim()) : null);
  const [gratuity, setGratuity] = useState(gratuityDefaultUsdc);
  const [boonBalance, setBoonBalance] = useState<bigint | null>(null);
  const [usdcBalance, setUsdcBalance] = useState<bigint | null>(null);
  const [status, setStatus] = useState<"idle" | "approving-boon" | "approving-usdc" | "recognizing" | "done">("idle");
  const [error, setError] = useState<string | null>(null);

  const gratuityAtomic = useMemo(() => {
    try {
      const v = parseUnits((gratuity || "0").trim(), 6);
      return v > 0n ? v : null;
    } catch {
      return null;
    }
  }, [gratuity]);

  const refreshBalances = useCallback(async () => {
    if (!address || !boon || !usdc) return;
    try {
      const [b, u] = await Promise.all([
        readContract(config, { address: boon, abi: erc20Abi, functionName: "balanceOf", args: [address], chainId: base.id }) as Promise<bigint>,
        readContract(config, { address: usdc, abi: erc20Abi, functionName: "balanceOf", args: [address], chainId: base.id }) as Promise<bigint>,
      ]);
      setBoonBalance(b);
      setUsdcBalance(u);
    } catch { /* balances are advisory */ }
  }, [address, boon, usdc, config]);

  useEffect(() => { void refreshBalances(); }, [refreshBalances]);

  const enoughBoon = boonBalance !== null && boonBalance >= BURN_AMOUNT;
  const enoughUsdc = usdcBalance !== null && gratuityAtomic !== null && usdcBalance >= gratuityAtomic;

  async function recognize() {
    if (!address || !routedBoon || !usdc || !boon || !resolvedPayTo || !gratuityAtomic) return;
    setError(null);
    try {
      if (chainId !== base.id) {
        setError("Switch your wallet to Base to continue.");
        return;
      }
      // 1) Approve the 100k $BOON burn if the allowance doesn't cover it.
      const boonAllowance = await readContract(config, { address: boon, abi: erc20Abi, functionName: "allowance", args: [address, routedBoon], chainId: base.id }) as bigint;
      if (boonAllowance < BURN_AMOUNT) {
        setStatus("approving-boon");
        const tx = await writeContract(config, { address: boon, abi: erc20Abi, functionName: "approve", args: [routedBoon, BURN_AMOUNT], chainId: base.id });
        await waitForTransactionReceipt(config, { hash: tx, chainId: base.id });
      }
      // 2) Approve the USDC gratuity if needed.
      const usdcAllowance = await readContract(config, { address: usdc, abi: erc20Abi, functionName: "allowance", args: [address, routedBoon], chainId: base.id }) as bigint;
      if (usdcAllowance < gratuityAtomic) {
        setStatus("approving-usdc");
        const tx = await writeContract(config, { address: usdc, abi: erc20Abi, functionName: "approve", args: [routedBoon, gratuityAtomic], chainId: base.id });
        await waitForTransactionReceipt(config, { hash: tx, chainId: base.id });
      }
      // 3) Route the Boon: burns $BOON, sends the gratuity to the endpoint.
      setStatus("recognizing");
      const tx = await writeContract(config, {
        address: routedBoon,
        abi: routedBoonAbi,
        functionName: "sendRoutedBoon",
        args: [routeId as Hex, randomContextRef(), getAddress(resolvedPayTo), gratuityAtomic, "0x0000000000000000000000000000000000000000", 0n],
        chainId: base.id,
      });
      await waitForTransactionReceipt(config, { hash: tx, chainId: base.id });
      setStatus("done");
      void refreshBalances();
      // Record the plaintext URL so this endpoint shows up in discovery once it
      // indexes, without any curation. Best-effort; the burn already succeeded.
      void captureX402EndpointUrl(endpointUrl, method).catch(() => {});
      onRecognized();
    } catch (cause) {
      setStatus("idle");
      setError(cause instanceof Error ? cause.message : String(cause));
    }
  }

  if (!routedBoon || !usdc || !boon) {
    return <p className="mt-3 text-xs text-muted">Endpoint recognition isn't configured in this build.</p>;
  }

  const working = status !== "idle" && status !== "done";

  return (
    <div className="mt-4">
      <p className="text-sm text-muted leading-relaxed">
        {endpointUrl} isn't recognized yet. Recognizing it sends a <span className="text-ink">routed Boon</span>: it burns a fixed <span className="text-ink">{formatBoon(BURN_AMOUNT)} $BOON</span> and sends a small USDC gratuity to the endpoint. That on-chain signal seeds it in the graph, so it becomes discoverable and anyone can review it. This is the strongest, ranked signal.
      </p>

      {isX402 === false && (
        <p className="mt-3 text-sm text-danger leading-relaxed">
          {endpointUrl} did not return an x402 payment challenge, so it does not look like a paid x402 endpoint. Only paid x402 endpoints can be recognized. If you know its payout address, you can still recognize it manually below.
        </p>
      )}

      {!endpointPayTo && (
        <label className="mt-4 block text-xs text-muted btn-mono max-w-xl">
          Endpoint payout address {isX402 !== false && "(could not auto-detect from the x402 challenge)"}
          <input
            className="mt-1 w-full border border-faint rounded-md bg-paper px-3 py-2 text-ink font-mono"
            value={manualPayTo}
            placeholder="0x…"
            onChange={(e) => setManualPayTo(e.target.value)}
          />
        </label>
      )}

      <div className="mt-4 grid gap-3 sm:grid-cols-2 max-w-xl">
        <label className="block text-xs text-muted btn-mono">
          USDC gratuity to the endpoint
          <div className="mt-1 flex items-center gap-2">
            <span className="text-ink">$</span>
            <input
              className="w-28 border border-faint rounded-md bg-paper px-3 py-2 text-ink"
              value={gratuity}
              inputMode="decimal"
              onChange={(e) => setGratuity(e.target.value)}
            />
          </div>
        </label>
        <div className="text-xs text-muted btn-mono self-end">
          <div>Your $BOON: {boonBalance === null ? "…" : `${formatBoon(boonBalance)}`} {!enoughBoon && boonBalance !== null && <span className="text-danger">(need {formatBoon(BURN_AMOUNT)})</span>}</div>
          <div>Your USDC: {usdcBalance === null ? "…" : `$${(Number(usdcBalance) / 1e6).toFixed(2)}`}</div>
        </div>
      </div>

      {!enoughBoon && boonBalance !== null && (
        <a href={BUY_BOON_URL} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex btn-mono text-xs rounded-md border border-faint px-3 py-2 text-olive-deep hover:border-ink-soft">
          Buy $BOON on Uniswap ↗
        </a>
      )}

      {!isConnected || !address ? (
        <div className="mt-4 space-y-3">
          <p className="text-sm text-muted">Connect the wallet holding your $BOON to recognize this endpoint.</p>
          <div className="flex flex-wrap items-center gap-2">
            <span className="btn-mono text-xs text-muted">Connect wallet</span>
            {connectChoices.map((choice) => (
              <button
                key={choice.kind}
                type="button"
                onClick={() => void connectWallet(choice)}
                disabled={connecting !== null}
                className="btn-mono text-xs px-2 py-1 rounded border border-faint text-muted hover:text-ink hover:border-ink-soft disabled:opacity-60"
              >
                {connecting === choice.kind ? "Connecting…" : choice.label}
              </button>
            ))}
          </div>
          {connectError && <p className="text-xs text-danger break-words">{connectError}</p>}
        </div>
      ) : (
        <div className="mt-4 space-y-3">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted">
            <span>Wallet: <span className="text-ink">{connector?.name ?? "wallet"}</span> ({address.slice(0, 6)}…{address.slice(-4)}).</span>
            <span className="flex items-center gap-1">
              Switch:
              {connectChoices.map((choice) => (
                <button key={choice.kind} type="button" onClick={() => void connectWallet(choice)} disabled={connecting !== null} className="btn-mono rounded border border-faint px-1.5 py-0.5 text-olive-deep hover:border-ink-soft disabled:opacity-60">
                  {connecting === choice.kind ? "…" : choice.label}
                </button>
              ))}
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              className="btn btn-primary"
              onClick={recognize}
              disabled={working || !resolvedPayTo || !gratuityAtomic || !enoughBoon || !enoughUsdc}
            >
              {status === "approving-boon" ? "Approving $BOON…" : status === "approving-usdc" ? "Approving USDC…" : status === "recognizing" ? "Recognizing…" : `Recognize (burn ${formatBoon(BURN_AMOUNT)} $BOON + $${gratuity} USDC)`}
            </button>
            {!enoughBoon && boonBalance !== null && <span className="text-xs text-danger">Not enough $BOON to burn.</span>}
            {enoughBoon && !enoughUsdc && <span className="text-xs text-danger">Not enough USDC for the gratuity.</span>}
          </div>
        </div>
      )}
      {status === "done" && (
        <div className="mt-4 space-y-4">
          <div className="animate-fade-up border border-olive/40 bg-olive-soft/30 rounded-md p-4">
            <div className="flex items-start gap-3">
              <span className="animate-check-pop mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-olive text-paper">
                <svg viewBox="0 0 20 20" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M4 10.5l3.5 3.5L16 5.5" /></svg>
              </span>
              <div>
                <p className="text-sm font-display text-ink">Recognized on-chain</p>
                <p className="mt-0.5 text-sm text-muted">The routed Boon landed. Indexing takes a moment. Two optional next steps:</p>
              </div>
            </div>
          </div>

          <div className="border border-faint rounded-md p-4">
            <p className="btn-mono text-[11px] uppercase tracking-[0.14em] text-muted">Step 1 · Describe it (makes it searchable)</p>
            <X402PublishDetails endpointUrl={endpointUrl} method={method} usdc={usdc} />
          </div>

          {contract && (
            <div className="border border-faint rounded-md p-4">
              <p className="btn-mono text-[11px] uppercase tracking-[0.14em] text-muted">Step 2 · Review it (what it was useful for)</p>
              <p className="mt-1 text-sm text-muted leading-relaxed">
                A wallet-signed review is your judgment of the endpoint. If it just indexed, give it a minute before publishing.
              </p>
              <X402ReviewComposer
                key={`recognize-review-${routeId}`}
                contract={contract}
                routeId={routeId}
                routeUrl={endpointUrl}
                endpointMethod={method}
                receiptPayTo=""
                receiptSigner={null}
                receiptStatus="not_observed"
                recognitions={null}
                reviews={[]}
                onPublished={onRecognized}
              />
            </div>
          )}
        </div>
      )}
      {error && <p className="mt-3 text-sm text-danger break-words">{error}</p>}
    </div>
  );
}
